import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Info from '@mui/icons-material/Info';
import { LoadingButton } from '@mui/lab';
import { Dialog } from '../../../components/Dialog';
import {
  getFarmqaFields,
  syncFarmqaFields,
} from '../../../services/admin-service';
import { useAlerts } from '../../../hooks/use-alerts';
import {
  Table,
  GridActionsCellItem,
  TableCell,
} from '../../../components/table/Table';
import { TableStatus } from '../../../components/table/TableStatus';
import { Field } from './Field';

export function Operation(props) {
  const { operation, onClose, year } = props;
  const [fields, setFields] = useState();
  const [field, setField] = useState();
  const [selected, setSelected] = useState([]);
  const [isSyncing, setSyncing] = useState(false);
  const { showError, showInfo } = useAlerts();

  const loadFields = useCallback(
    () => getFarmqaFields(operation.id).then(setFields).catch(showError),
    [operation.id, showError],
  );

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  return (
    <>
      {buildFieldDetails()}
      <Dialog
        title={operation.name}
        width="lg"
        actions={false}
        onCancel={onClose}
      >
        <Box sx={{ height: 'calc(100vh - 400px)' }}>{buildFields()}</Box>
      </Dialog>
    </>
  );

  function buildFieldDetails() {
    if (!field) return null;
    return <Field field={field} onClose={() => setField(null)} />;
  }

  function buildFields() {
    return (
      <Table
        loading={!fields}
        columns={buildColumns()}
        rows={fields || []}
        hideFooter={false}
        checkboxSelection
        rowSelectionModel={selected}
        onRowSelectionModelChange={setSelected}
        initialState={{
          sorting: {
            sortModel: [{ field: 'fieldName', sort: 'asc' }],
          },
          filter: {
            filterModel: {
              items: [{ field: 'year', operator: '=', value: year }],
            },
          },
        }}
        actions={[
          <LoadingButton
            key="sync"
            onClick={syncSelected}
            disabled={!selected.length}
            loading={isSyncing}
          >
            Sync
          </LoadingButton>,
        ]}
      />
    );
  }

  function buildColumns() {
    return [
      {
        field: 'fieldName',
        headerName: 'Field Name',
        flex: 1.5,
        minWidth: 100,
        renderCell: (params) => (
          <TableCell
            copyOnClick={params.row.aaManagedId}
            renderCellParams={params}
          />
        ),
      },
      {
        field: 'farmName',
        headerName: 'Farm Name',
        flex: 1,
      },
      {
        field: 'fieldId',
        headerName: 'Field ID',
        type: 'number',
        flex: 0.75,
        minWidth: 75,
        valueFormatter: ({ value }) => value && `${value}`,
      },
      {
        field: 'acres',
        headerName: 'Acres',
        type: 'number',
        flex: 0.75,
        minWidth: 75,
        valueFormatter: ({ value }) =>
          Number(value)
            ? Number(value).toLocaleString('en-US', {
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        field: 'year',
        headerName: 'Year',
        type: 'number',
        flex: 1,
        valueFormatter: ({ value }) => value && `${value}`,
      },
      {
        field: 'syncStatus',
        headerName: 'Status',
        flex: 0.75,
        renderCell: ({ row }) => <TableStatus status={row.syncStatus} />,
      },
      {
        field: 'syncedAt',
        headerName: 'Synced At',
        flex: 1,
        type: 'dateTime',
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleString() : '--',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<Info />}
            label="Details"
            disabled={!row.syncedAt}
            onClick={() => setField(row)}
          />,
        ],
      },
    ];
  }

  function syncSelected() {
    setSyncing(true);
    syncFarmqaFields(
      selected.map((id) => fields.find((f) => f.id === id).aaid),
      year,
    )
      .then(() => {
        loadFields();
        showInfo('Sync schedule');
      })
      .catch(showError)
      .finally(() => setSyncing(false));
  }
}
